// The order of the change-over from a won Sight Reading tier to what the
// player picks next.
//
// A tier is won when the count toward the next tier reaches 0. From there the
// screen goes, in this order and each step once:
//
//   1. won: playback stops and the tier-complete overlay dims the finished tier
//      while its completion is recorded and the wrap-up is fetched;
//   2. wrap-up: the completion modal opens on the finished tier's summary;
//   3. choice: Replay Tier or Next asks for the play session that follows
//      (beginWonTierChoice, then the caller's request). The modal stays up, on
//      the finished tier's summary, until that session has arrived; a second
//      press while it is on its way is ignored;
//   4. arrival: the modal closes and the finished tier's wrap-up state is
//      cleared in one step. The overlay holds (the count stays at 0) until the
//      lesson has torn the finished tier down for the new session, which sets
//      the count back to its start, so nothing of the finished tier shows, and
//      nothing can be played, in between.
//
// Next at the level's last tier leaves the lesson instead (step 3 only), once.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as lessonActions from 'Actions/lesson';
import { AppDispatch } from 'Reducers';
import { LessonReducer } from 'Reducers/lessonReducer';
import { MIN_NUM_PHRASES } from 'Utils/Constants';

/**
 * How long the overlay may hold after the next session has arrived. The
 * lesson's own reset ends the hold; if that reset fails (its tier list request
 * throws), the overlay is lifted after this so the header's Back is not left
 * covered.
 */
export const TIER_OVERLAY_SAFETY_RELEASE_MS = 10000;

interface TierCompleteTransitionInput {
  /**
   * Clears the lesson screen's own state for the finished tier's wrap-up (its
   * carousel, the repertoire it unlocked, the accuracy readout). Called once,
   * when the next session has arrived.
   */
  onNextSessionArrived: () => void;
}

interface TierCompleteTransition {
  /**
   * Claims the player's choice after a won tier (Replay, Next): true the first
   * time, and the caller then asks for the next session or leaves; false while
   * an earlier choice is still under way, and the caller does nothing.
   */
  beginWonTierChoice: () => boolean;
}

export const useTierCompleteTransition = ({
  onNextSessionArrived,
}: TierCompleteTransitionInput): TierCompleteTransition => {
  const dispatch = useDispatch<AppDispatch>();
  const playSessionId = useSelector(
    (state: LessonReducer) => state.lessonReducer.currentUserLevelData?.play_session?.id ?? null,
  );
  const phrasesTilNextTier = useSelector(
    (state: LessonReducer) => state.lessonReducer.phrasesTilNextTier,
  );
  const hasTierSummary = useSelector(
    (state: LessonReducer) => state.lessonReducer.tierSummary !== null,
  );

  // The session a choice was made on, while its follower is on its way. A ref,
  // so two presses in one frame cannot both get through.
  const choiceMadeOnRef = useRef<string | null>(null);
  // The session that arrived after a choice, while the overlay still holds for it.
  const [holdingOverlayFor, setHoldingOverlayFor] = useState<string | null>(null);
  const onNextSessionArrivedRef = useRef(onNextSessionArrived);
  onNextSessionArrivedRef.current = onNextSessionArrived;

  // 1. Won: playback stops. Stopped, not toggled: a tier won while the lesson
  // was already paused stays paused.
  useEffect(() => {
    if (phrasesTilNextTier === 0) {
      dispatch(lessonActions.setLessonPlaying({ lessonPlaying: false }));
    }
  }, [phrasesTilNextTier, dispatch]);

  // 4. Arrival, and any other change of session: the wrap-up shown belongs to
  // the session that was left.
  const previousSessionRef = useRef(playSessionId);
  useEffect(() => {
    const previousSession = previousSessionRef.current;
    previousSessionRef.current = playSessionId;
    if (playSessionId === previousSession) return;
    const choiceMadeOn = choiceMadeOnRef.current;
    if (choiceMadeOn !== null && playSessionId && playSessionId !== choiceMadeOn) {
      choiceMadeOnRef.current = null;
      dispatch(lessonActions.setShowWrapUp(false));
      dispatch(lessonActions.setWrapUpIndex(0));
      dispatch(lessonActions.setTierSummary(null));
      dispatch(lessonActions.setTierSummaryRequestError(false));
      dispatch(lessonActions.resetCompletedPhrasesCount());
      onNextSessionArrivedRef.current();
      setHoldingOverlayFor(playSessionId);
      return;
    }
    if (hasTierSummary) dispatch(lessonActions.setTierSummary(null));
  }, [playSessionId, hasTierSummary, dispatch]);

  // The overlay's hold ends when the count is back at its start; the safety
  // release lifts it if that never happens.
  useEffect(() => {
    if (holdingOverlayFor === null) return undefined;
    if (phrasesTilNextTier !== 0 || playSessionId !== holdingOverlayFor) {
      setHoldingOverlayFor(null);
      return undefined;
    }
    const release = setTimeout(() => {
      dispatch(lessonActions.updatePhrasesTilNextTier(MIN_NUM_PHRASES));
      setHoldingOverlayFor(null);
    }, TIER_OVERLAY_SAFETY_RELEASE_MS);
    return () => clearTimeout(release);
  }, [holdingOverlayFor, phrasesTilNextTier, playSessionId, dispatch]);

  // 3. Choice.
  const beginWonTierChoice = useCallback(() => {
    if (choiceMadeOnRef.current !== null) return false;
    choiceMadeOnRef.current = playSessionId ?? '';
    // Nothing plays while the finished tier waits for what follows it: a
    // session arriving while play was on would be taken for a won tier.
    dispatch(lessonActions.setLessonPlaying({ lessonPlaying: false }));
    return true;
  }, [playSessionId, dispatch]);

  return { beginWonTierChoice };
};
